import { useState, useEffect } from 'react';
import { Star, Sparkles, Home, Building, Leaf, Eye, ArrowRight, Calendar, Trophy, Zap, Phone, Grid, List, CheckCircle, MapPin, Clock, Users, Award, ExternalLink, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { useLocation, Link } from 'react-router-dom';

const CLOUDINARY_BASE = "https://res.cloudinary.com/dpk0ydobt/image/upload/q_auto,f_auto/placof/";

const ProjectsPage = () => {
  const { pathname } = useLocation();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    // Scroll au haut de la page à chaque changement d'URL
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Intersection Observer pour les animations d'apparition
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting
          }));
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const categories = [
    { id: 'all', name: 'Tous les Projets', icon: Star, color: 'from-amber-500 to-orange-500' },
    { id: 'interior', name: 'Intérieur', icon: Home, color: 'from-blue-500 to-cyan-500' },
    { id: 'commercial', name: 'Commercial', icon: Building, color: 'from-amber-500 to-orange-500' },
    { id: 'exterior', name: 'Extérieur', icon: Leaf, color: 'from-green-500 to-emerald-500' }
  ];

  const projects = [
    {
      id: 1,
      title: "Salle des fêtes Layelina",
      category: "interior",
      location: "Sfax",
      description: "Transformation complète d'une salle des fêtes avec un design moderne et élégant. Création d'un espace festif chaleureux avec un éclairage sophistiqué et un aménagement optimisé pour les événements.",
      features: ["Éclairage LED ambiance", "Décoration moderne", "Aménagement optimal", "Design festif"],
      images: [
        `${CLOUDINARY_BASE}projets/Salle des fêtes Layelina.jpg`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Layelina-1.jpg`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Layelina-2.jpg`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Layelina-3.jpg`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Layelina-4.png`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Layelina-5.png`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Layelina-6.png`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Layelina-7.png`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Layelina-8.png`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Layelina-9.jpg`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Layelina-10.jpg`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Layelina-11.jpg`,
      ],
      thumbnail: `${CLOUDINARY_BASE}projets/Salle des fêtes Layelina.jpg`,
      tags: ["Moderne", "Festif", "Élégant"],
    },
    {
      id: 2,
      title: "Salle des fêtes Layeli Palace",
      category: "commercial",
      location: "Sfax",
      description: "Conception d'une salle de réception luxueuse avec un style palace raffiné. Intégration d'éléments décoratifs sophistiqués et d'un système d'éclairage premium pour créer une ambiance exceptionnelle.",
      features: ["Design palace", "Éclairage premium", "Décoration luxueuse", "Ambiance raffinée"],
      images: [
        `${CLOUDINARY_BASE}projets/Salle des fêtes Layeli.jpg`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Layeli-1.png`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Layeli-2.png`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Layeli-4.jpg`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Layeli-5.jpg`,
      ],
      thumbnail: `${CLOUDINARY_BASE}projets/Salle des fêtes Layeli.jpg`,
      tags: ["Luxueux", "Palace", "Raffiné"],
    },
    {
      id: 3,
      title: "Salle des fêtes Cité El Ons",
      category: "commercial",
      location: "Sfax",
      description: "Aménagement d'une salle des fêtes moderne dans un quartier résidentiel. Focus sur la fonctionnalité et l'esthétique avec des solutions d'éclairage innovantes et un design contemporain.",
      features: ["Design contemporain", "Solutions innovantes", "Fonctionnalité optimale", "Éclairage moderne"],
      images: [
        `${CLOUDINARY_BASE}projets/Salle des fêtes Cité El Ons.jpg`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Cité El Ons-1.jpg`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Cité El Ons-2.jpg`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Cité El Ons-3.jpg`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Cité El Ons-4.jpg`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Cité El Ons-5.jpg`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Cité El Ons-6.jpg`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Cité El Ons-7.jpg`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Cité El Ons-8.jpg`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Cité El Ons-9.jpg`,
        `${CLOUDINARY_BASE}projets/Salle des fêtes Cité El Ons-10.jpg`,
      ],
      thumbnail: `${CLOUDINARY_BASE}projets/Salle des fêtes Cité El Ons.jpg`,
      tags: ["Contemporain", "Innovant", "Fonctionnel"],
    },
    {
      id: 4,
      title: "Café Le Saphir Bleu",
      category: "exterior",
      location: "Mahdia",
      description: "Création d'un espace café extérieur avec une ambiance méditerranéenne unique. Aménagement de terrasses avec éclairage d'ambiance et mobilier adapté au climat côtier.",
      features: ["Terrasse méditerranéenne", "Éclairage extérieur", "Mobilier résistant", "Ambiance côtière"],
      images: [
        `${CLOUDINARY_BASE}projets/Café Le Saphir Bleu.jpg`,
        `${CLOUDINARY_BASE}projets/Café Le Saphir Bleu-1.jpg`,
        `${CLOUDINARY_BASE}projets/Café Le Saphir Bleu-2.jpg`,
        `${CLOUDINARY_BASE}projets/Café Le Saphir Bleu-3.jpg`,
        `${CLOUDINARY_BASE}projets/Café Le Saphir Bleu-4.jpg`,
        `${CLOUDINARY_BASE}projets/Café Le Saphir Bleu-5.jpg`,
        `${CLOUDINARY_BASE}projets/Café Le Saphir Bleu-6.jpg`,
        `${CLOUDINARY_BASE}projets/Café Le Saphir Bleu-7.jpg`,
        `${CLOUDINARY_BASE}projets/Café Le Saphir Bleu-8.jpg`,
        `${CLOUDINARY_BASE}projets/Café Le Saphir Bleu-9.jpg`,
        `${CLOUDINARY_BASE}projets/Café Le Saphir Bleu-10.jpg`,
      ],
      thumbnail: `${CLOUDINARY_BASE}projets/Café Le Saphir Bleu.jpg`,
      tags: ["Méditerranéen", "Terrasse", "Côtier"],
    },
    {
      id: 5,
      title: "Boutique HA Hamadi Abid",
      category: "interior",
      location: "Sfax, Gafsa, Tunis",
      description: "Aménagement de boutiques modernes avec un design commercial attractif. Optimisation de l'espace de vente avec un éclairage professionnel et une présentation produit optimale.",
      features: ["Design commercial", "Éclairage professionnel", "Optimisation vente", "Présentation produit"],
      images: [
        `${CLOUDINARY_BASE}projets/HA.png`,
        `${CLOUDINARY_BASE}projets/HA-1.jpg`,
        `${CLOUDINARY_BASE}projets/HA-2.jpg`,
        `${CLOUDINARY_BASE}projets/HA-3.jpg`,
        `${CLOUDINARY_BASE}projets/HA-4.jpg`,
        `${CLOUDINARY_BASE}projets/HA-5.jpg`,
        `${CLOUDINARY_BASE}projets/HA-6.jpg`,
        `${CLOUDINARY_BASE}projets/HA-7.jpg`,
        `${CLOUDINARY_BASE}projets/HA-8.jpg`,
        `${CLOUDINARY_BASE}projets/HA-9.jpg`,
        `${CLOUDINARY_BASE}projets/HA-10.jpg`,
        `${CLOUDINARY_BASE}projets/HA-11.jpg`,
      ],
      thumbnail: `${CLOUDINARY_BASE}projets/HA.png`,
      tags: ["Commercial", "Moderne", "Optimisé"],
    },
    {
      id: 6,
      title: "ABC SOMOCER Group Abdennadher",
      category: "commercial",
      location: "Sfax, Gabes, Hammamet, Tunis",
      description: "Aménagement de showrooms pour le groupe SOMOCER avec un design industriel moderne. Mise en valeur des produits céramiques avec un éclairage technique et des espaces d'exposition optimisés.",
      features: ["Design industriel", "Éclairage technique", "Exposition optimisée", "Mise en valeur produits"],
      images: [
        `${CLOUDINARY_BASE}projets/ABC.png`,
        `${CLOUDINARY_BASE}projets/ABC-1.png`,
        `${CLOUDINARY_BASE}projets/ABC-2.png`,
        `${CLOUDINARY_BASE}projets/ABC-3.jpg`,
        `${CLOUDINARY_BASE}projets/ABC-4.jpg`,
        `${CLOUDINARY_BASE}projets/ABC-5.jpg`,
        `${CLOUDINARY_BASE}projets/ABC-6.jpg`,
        `${CLOUDINARY_BASE}projets/ABC-7.jpg`,
        `${CLOUDINARY_BASE}projets/ABC-8.jpg`,
        `${CLOUDINARY_BASE}projets/ABC-9.jpg`,
        `${CLOUDINARY_BASE}projets/ABC-10.jpg`,
        `${CLOUDINARY_BASE}projets/ABC-11.jpg`,
        `${CLOUDINARY_BASE}projets/ABC-12.jpg`,
        `${CLOUDINARY_BASE}projets/ABC-13.jpg`,
        `${CLOUDINARY_BASE}projets/ABC-14.jpg`,
        `${CLOUDINARY_BASE}projets/ABC-15.jpg`,
      ],
      thumbnail: `${CLOUDINARY_BASE}projets/ABC.png`,
      tags: ["Industriel", "Showroom", "Technique"],
    },
    {
      id: 7,
      title: "Show room ABID MED",
      category: "exterior",
      location: "Sfax",
      description: "Conception d'un showroom extérieur avec des espaces d'exposition à ciel ouvert. Intégration d'éléments décoratifs résistants et d'un système d'éclairage extérieur performant.",
      features: ["Exposition extérieure", "Éléments résistants", "Éclairage performant", "Design extérieur"],
      images: [
        `${CLOUDINARY_BASE}projets/Abid MED.jpg`,
        `${CLOUDINARY_BASE}projets/Abid MED-1.jpg`,
        `${CLOUDINARY_BASE}projets/Abid MED-2.jpg`,
        `${CLOUDINARY_BASE}projets/Abid MED-3.jpg`,
        `${CLOUDINARY_BASE}projets/Abid MED-4.jpg`,
        `${CLOUDINARY_BASE}projets/Abid MED-5.jpg`,
        `${CLOUDINARY_BASE}projets/Abid MED-6.jpg`,
        `${CLOUDINARY_BASE}projets/Abid MED-7.jpg`,
        `${CLOUDINARY_BASE}projets/Abid MED-8.jpg`,
        `${CLOUDINARY_BASE}projets/Abid MED-9.jpg`,
        `${CLOUDINARY_BASE}projets/Abid MED-10.jpg`,
      ],
      thumbnail: `${CLOUDINARY_BASE}projets/Abid MED.jpg`,
      tags: ["Extérieur", "Résistant", "Performant"],
    },
    {
      id: 8,
      title: "Hôtel Nour Palace",
      category: "exterior",
      location: "Mahdia",
      description: "Aménagement des espaces extérieurs d'un hôtel avec création de zones de détente et d'espaces de réception. Design luxueux adapté au secteur hôtelier avec éclairage d'ambiance.",
      features: ["Zones de détente", "Espaces réception", "Design hôtelier", "Éclairage d'ambiance"],
      images: [
        `${CLOUDINARY_BASE}projets/Hôtel Nour Palace.jpg`,
        `${CLOUDINARY_BASE}projets/Hôtel Nour Palace-1.jpg`,
        `${CLOUDINARY_BASE}projets/Hôtel Nour Palace-2.jpg`,
        `${CLOUDINARY_BASE}projets/Hôtel Nour Palace-3.jpg`,
        `${CLOUDINARY_BASE}projets/Hôtel Nour Palace-4.jpg`,
        `${CLOUDINARY_BASE}projets/Hôtel Nour Palace-5.jpg`,
        `${CLOUDINARY_BASE}projets/Hôtel Nour Palace-6.jpg`,
        `${CLOUDINARY_BASE}projets/Hôtel Nour Palace-7.jpg`,
        `${CLOUDINARY_BASE}projets/Hôtel Nour Palace-8.jpg`,
      ],
      thumbnail: `${CLOUDINARY_BASE}projets/Hôtel Nour Palace.jpg`,
      tags: ["Hôtelier", "Détente", "Luxueux"],
    },
    {
      id: 9,
      title: "Villa, Appartements, Locaux commerciaux, Restaurants, Terrasses",
      category: "exterior",
      location: "Mahdia",
      description: "Projets multiples comprenant villas, appartements, locaux commerciaux, restaurants et terrasses. Approche personnalisée pour chaque type d'espace avec des solutions d'aménagement adaptées.",
      features: ["Projets multiples", "Approche personnalisée", "Solutions adaptées", "Diversité d'espaces"],
      images: [
        `${CLOUDINARY_BASE}projets-gen/villa.jpeg`,
        `${CLOUDINARY_BASE}projets-gen/rest.jpeg`,
        `${CLOUDINARY_BASE}projets-gen/int.jpeg`,
        `${CLOUDINARY_BASE}projets-gen/comm-1.jpeg`,
        `${CLOUDINARY_BASE}projets-gen/comm-2.jpeg`,
        `${CLOUDINARY_BASE}projets-gen/int-1.jpeg`,
        `${CLOUDINARY_BASE}projets-gen/int-2.jpeg`,
        `${CLOUDINARY_BASE}projets-gen/int-3.jpeg`,
        `${CLOUDINARY_BASE}projets-gen/int-4.jpeg`,
        `${CLOUDINARY_BASE}services/eclairage.jpeg`,
        `${CLOUDINARY_BASE}services/dec-int.jpeg`
      ],
      thumbnail: `${CLOUDINARY_BASE}services/dec-int.jpeg`,
      tags: ["Diversifié", "Personnalisé", "Adapté"],
    }
  ];

  const filteredProjects = selectedCategory === 'all'
    ? projects
    : projects.filter(project => project.category === selectedCategory);

  const stats = [
    { icon: Trophy, title: "Projets Réalisés", value: "150+", color: "from-amber-500 to-orange-500" },
    { icon: Users, title: "Clients Satisfaits", value: "97%", color: "from-blue-500 to-cyan-500" },
    { icon: Award, title: "Prix Remportés", value: "15", color: "from-green-500 to-emerald-500" },
    { icon: Clock, title: "Années d'Expérience", value: "13+", color: "from-purple-500 to-pink-500" }
  ];

  const openModal = (project, imageIndex = 0) => {
    setSelectedProject(project);
    setCurrentImageIndex(imageIndex);
  };

  const closeModal = () => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prev) =>
        prev === selectedProject.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? selectedProject.images.length - 1 : prev - 1
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>Nos Réalisations - Placof Décor | Galerie de Projets</title>
        <meta
          name="description"
          content="Explorez nos réalisations en décoration intérieure, faux plafonds et placoplâtre à Sfax. Inspirez-vous de nos projets récents."
        />
        <meta name="keywords" content="projets décoration Sfax, galerie faux plafonds, réalisations Placof Décor" />

        {/* Open Graph */}
        <meta property="og:title" content="Nos Réalisations - Placof Décor | Galerie de Projets" />
        <meta property="og:description" content="Découvrez nos projets récents en décoration intérieure et faux plafonds." />

        <link rel="canonical" href={`https://placofdecor.tn${pathname}`} />

      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50 overflow-hidden">
        {/* Particules de fond */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
          {[...Array(40)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full opacity-20 animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${i * 0.2}s`,
                transform: `translate(${mousePosition.x * 0.01}px, ${mousePosition.y * 0.005}px)`
              }}
            />
          ))}
        </div>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto text-center relative z-10">
            <div
              className="space-y-8"
              style={{
                transform: `translateY(${scrollY * 0.1}px)`
              }}
            >
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-orange-100 px-6 py-3 rounded-full border border-amber-200/50 shadow-lg">
                <Sparkles className="w-5 h-5 text-amber-600 animate-pulse" />
                <span className="text-amber-700 font-semibold">Nos Réalisations</span>
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent leading-tight">
                Projets &
                <span className="block text-4xl sm:text-5xl lg:text-6xl mt-2">
                  Réalisations
                </span>
              </h1>

              <p className="text-xl sm:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                Découvrez nos projets les plus marquants et laissez-vous inspirer par nos
                <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent font-semibold"> créations exceptionnelles</span>.
              </p>

              {/* Statistiques */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className="group relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-500 border border-amber-100/50 transform hover:scale-105"
                  >
                    <div className={`w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-r ${stat.color} flex items-center justify-center group-hover:rotate-12 transition-transform duration-300`}>
                      <stat.icon className="w-6 h-6 text-white group-hover:animate-bounce" />
                    </div>
                    <div className={`text-2xl font-black bg-gradient-to-r ${stat.color} bg-clip-text text-transparent mb-1`}>
                      {stat.value}
                    </div>
                    <div className="text-gray-600 text-sm font-semibold">
                      {stat.title}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Élément décoratif */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-full blur-3xl -z-10" />
        </section>

        {/* Filtres et Controls */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-amber-50/50 to-orange-50/50">
          <div className="container mx-auto">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-8">
              {/* Filtres par catégorie */}
              <div className="flex flex-wrap gap-4">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`group flex items-center gap-3 px-6 py-3 rounded-2xl font-semibold transition-all duration-500 transform hover:scale-105 ${selectedCategory === category.id
                      ? `bg-gradient-to-r ${category.color} text-white shadow-xl`
                      : 'bg-white/80 backdrop-blur-sm text-gray-700 shadow-lg hover:shadow-xl border border-amber-100/50'
                      }`}
                  >
                    <category.icon className="w-5 h-5 group-hover:animate-bounce" />
                    <span>{category.name}</span>
                  </button>
                ))}
              </div>

              {/* Mode d'affichage */}
              <div className="flex items-center gap-4">
                <div className="flex bg-white/80 backdrop-blur-sm rounded-2xl p-1 shadow-lg border border-amber-100/50">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-3 rounded-xl transition-all duration-300 ${viewMode === 'grid'
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg'
                      : 'text-gray-600 hover:bg-amber-50'
                      }`}
                  >
                    <Grid className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-3 rounded-xl transition-all duration-300 ${viewMode === 'list'
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg'
                      : 'text-gray-600 hover:bg-amber-50'
                      }`}
                  >
                    <List className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Grille des Projets */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto">
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProjects.map((project, index) => (
                  <div
                    key={project.id}
                    id={`project-${project.id}`}
                    data-animate
                    className={`group relative bg-white/80 backdrop-blur-sm rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105 ${isVisible[`project-${project.id}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                      }`}
                    style={{
                      transitionDelay: `${index * 0.1}s`
                    }}
                  >
                    {/* Image */}
                    <div className="relative overflow-hidden h-64">
                      <img
                        src={project.thumbnail}
                        alt={project.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                      {/* Overlay avec boutons */}
                      <div className="absolute inset-0 flex items-center justify-center gap-4 opacity-0 group-hover:opacity-100 transition-all duration-500">
                        <button
                          onClick={() => openModal(project)}
                          className="bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110"
                        >
                          <Eye className="w-5 h-5 text-amber-600" />
                        </button>
                        <button
                          onClick={() => openModal(project)}
                          className="bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110"
                        >
                          <ExternalLink className="w-5 h-5 text-amber-600" />
                        </button>
                      </div>




                    </div>

                    {/* Contenu */}
                    <div className="p-6 space-y-4">
                      <div className="space-y-2">
                        <h3 className="text-xl font-bold text-gray-800 group-hover:text-amber-600 transition-colors">
                          {project.title}
                        </h3>
                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                          <MapPin className="w-4 h-4" />
                          <span>{project.location}</span>
                        </div>
                      </div>

                      <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                        {project.description}
                      </p>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2">
                        {project.tags.map((tag, tagIndex) => (
                          <span
                            key={tagIndex}
                            className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-xs font-semibold"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-4 border-t border-amber-100">
                        <div className="text-sm text-gray-500">

                        </div>
                        <button
                          onClick={() => openModal(project)}
                          className="group/btn flex items-center gap-2 text-amber-600 hover:text-amber-700 font-semibold text-sm transition-colors"
                        >
                          <span>Voir détails</span>
                          <ArrowRight className="w-4 h-4 transform group-hover/btn:translate-x-1 transition-transform" />
                        </button>
                      </div>
                    </div>

                    {/* Effet de brillance */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  </div>
                ))}
              </div>
            ) : (
              /* Vue Liste */
              <div className="space-y-8">
                {filteredProjects.map((project, index) => (
                  <div
                    key={project.id}
                    id={`project-list-${project.id}`}
                    data-animate
                    className={`group relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-amber-100/50 ${isVisible[`project-list-${project.id}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                      }`}
                    style={{
                      transitionDelay: `${index * 0.1}s`
                    }}
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                      {/* Image */}
                      <div className="relative overflow-hidden rounded-2xl h-48 lg:h-32">
                        <img
                          src={project.thumbnail}
                          alt={project.title}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                      </div>

                      {/* Contenu */}
                      <div className="lg:col-span-2 space-y-4">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                          <div className="space-y-2">
                            <h3 className="text-2xl font-bold text-gray-800 group-hover:text-amber-600 transition-colors">
                              {project.title}
                            </h3>
                            <div className="flex items-center gap-4 text-gray-500 text-sm">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4" />
                                <span>{project.location}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-4">

                            <button
                              onClick={() => openModal(project)}
                              className="group/btn flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 rounded-xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105"
                            >
                              <span>Voir projet</span>
                              <ArrowRight className="w-4 h-4 transform group-hover/btn:translate-x-1 transition-transform" />
                            </button>
                          </div>
                        </div>

                        <p className="text-gray-600 leading-relaxed">
                          {project.description}
                        </p>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-2">
                          {project.tags.map((tag, tagIndex) => (
                            <span
                              key={tagIndex}
                              className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-xs font-semibold"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Effet de brillance */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-3xl" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Modal de Détails du Projet */}
        {selectedProject && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              {/* Overlay */}
              <div
                className="fixed inset-0 transition-opacity"
                aria-hidden="true"
                onClick={closeModal}
              >
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>
              </div>

              {/* Contenu du Modal */}
              <div className="inline-block align-bottom bg-white/90 backdrop-blur-sm rounded-3xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
                <div className="relative">
                  {/* Bouton Fermer */}
                  <button
                    onClick={closeModal}
                    className="absolute top-4 right-4 z-10 bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110"
                  >
                    <X className="w-5 h-5 text-gray-700" />
                  </button>

                  {/* Carrousel d'Images */}
                  <div className="relative h-96 sm:h-[500px] overflow-hidden">
                    <img
                      src={selectedProject.images[currentImageIndex]}
                      alt={selectedProject.title}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />

                    {/* Boutons Navigation */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        prevImage();
                      }}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <ChevronLeft className="w-6 h-6 text-amber-600" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        nextImage();
                      }}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <ChevronRight className="w-6 h-6 text-amber-600" />
                    </button>

                    {/* Indicateurs d'Images */}
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                      {selectedProject.images.map((_, index) => (
                        <button
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentImageIndex(index);
                          }}
                          className={`w-3 h-3 rounded-full transition-all duration-300 ${currentImageIndex === index
                            ? 'bg-amber-600 w-6'
                            : 'bg-white/70 hover:bg-white'
                            }`}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Détails du Projet */}
                  <div className="p-6 sm:p-8 space-y-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <h3 className="text-2xl sm:text-3xl font-bold text-gray-800">
                          {selectedProject.title}
                        </h3>

                      </div>


                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                      <div className="lg:col-span-2 space-y-6">
                        <div>
                          <h4 className="text-xl font-semibold text-gray-800 mb-3">
                            Description
                          </h4>
                          <p className="text-gray-600 leading-relaxed">
                            {selectedProject.description}
                          </p>
                        </div>

                        <div>
                          <h4 className="text-xl font-semibold text-gray-800 mb-3">
                            Caractéristiques
                          </h4>
                          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {selectedProject.features.map((feature, index) => (
                              <li key={index} className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                                <span className="text-gray-600">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      <div className="space-y-6">


                        <div className="flex flex-wrap gap-3">
                          {selectedProject.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>

                        <button className="w-full group/btn flex items-center justify-center gap-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-4 rounded-xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105">
                          <Phone className="w-5 h-5 group-hover/btn:animate-bounce" />
                          <Link to="/contact"> <span>Contactez-nous pour un projet similaire</span> </Link>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CTA Final */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto text-center">
            <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-3xl p-12 shadow-2xl relative overflow-hidden">
              {/* Effet de brillance */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full animate-pulse" />

              <div className="relative z-10 space-y-8">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center">
                    <Zap className="w-10 h-10 text-white animate-pulse" />
                  </div>
                </div>

                <h2 className="text-4xl sm:text-5xl font-black text-white mb-6">
                  Prêt à Transformer Votre Espace ?
                </h2>

                <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
                  Discutons de votre projet et créons ensemble un lieu unique qui vous ressemble.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button className="group relative flex items-center gap-3 bg-white text-amber-600 px-8 py-4 rounded-2xl font-bold shadow-2xl hover:shadow-white/25 transition-all duration-500 overflow-hidden transform hover:scale-105">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                    <Calendar className="w-5 h-5 relative z-10 group-hover:animate-bounce" />
                    <span className="relative z-10">Consultation Gratuite</span>
                  </button>

                  <a
                    href="tel:+21697286007"
                    className="group flex items-center gap-3 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-2xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-500 border border-white/20 hover:border-white/40"
                  >
                    <Phone className="w-5 h-5 group-hover:animate-bounce" />
                    <span>+216 97 286 007</span>
                    <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProjectsPage;